import {
  ActionIcon,
  Box,
  Center,
  Flex,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import type { ReactNode } from "react";

import Dialog from "./Dialog";
import SvgIconRaw, { type IconName } from "./SvgIconRaw";

export type DetailDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The tile beside the title. */
  icon: IconName;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-hand header cluster, rendered before the close button. */
  headerActions?: ReactNode;
  /** Close button label — callers own the namespace it is translated in. */
  closeLabel: string;
  children: ReactNode;
};

/**
 * The full-width drill-down dialog: a primary accent bar, an icon/title/
 * subtitle header with the caller's controls and a close button on the right,
 * and a tinted body so the content below reads as a card on a page, matching
 * the page/card relationship everywhere else in the app.
 *
 * The body is a bounded flex column (the dialog's own height minus nothing),
 * so a `fillHeight` MeqTable or a virtualised grid inside it gets the definite
 * scroll parent it needs.
 */
const DetailDialog = ({
  isOpen,
  onClose,
  icon,
  title,
  subtitle,
  headerActions,
  closeLabel,
  children,
}: DetailDialogProps) => (
  <Dialog open={isOpen} onClose={onClose} fullWidth>
    {/* Height mirrors Dialog's `fullWidth` inset (2rem each side): the content
        needs a bounded scroll parent, so this cannot be auto. */}
    <Flex
      direction="column"
      style={{ height: "calc(100dvh - 4rem)", minHeight: 0 }}
    >
      <Box
        style={{
          flex: "0 0 auto",
          height: 6,
          backgroundColor: "var(--mantine-primary-color-filled)",
        }}
      />
      <Box
        px={16}
        py={8}
        style={{
          flex: "0 0 auto",
          borderBottom: "1px solid var(--mantine-color-border-secondary)",
        }}
      >
        <Group justify="space-between" align="center" gap={16} wrap="wrap">
          <Group gap={12} align="center" wrap="nowrap">
            <Center
              style={{
                flex: "0 0 auto",
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: "var(--mantine-primary-color-filled)",
                color: "var(--mantine-color-white)",
              }}
            >
              <SvgIconRaw name={icon} size={20} />
            </Center>
            <Stack gap={0}>
              <Text size="xl" fw={600}>
                {title}
              </Text>
              {subtitle !== undefined && (
                <Text size="sm" c="dimmed">
                  {subtitle}
                </Text>
              )}
            </Stack>
          </Group>
          <Group gap={24} align="center" wrap="wrap" justify="flex-end">
            {headerActions}
            <Tooltip label={closeLabel}>
              <ActionIcon
                aria-label={closeLabel}
                onClick={onClose}
                variant="secondary"
                size="input-sm"
              >
                <SvgIconRaw name="XClose" size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      <Flex
        direction="column"
        p={16}
        style={{
          flex: 1,
          minHeight: 0,
          backgroundColor: "var(--mantine-color-bg-secondary)",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </Flex>
    </Flex>
  </Dialog>
);

export default DetailDialog;
